import React from "react";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";
import { Button, styled } from "@mui/material";

export const styleClasses = {
  toggleBox: "toggleBox",
  toggleOn: "toggleOn",
  toggleOff: "toggleOff",
  box: "box",
  mainContainer: "mainContainer",
};

export const StyledToggleBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFromPreviewPopup",
})<{ isFromPreviewPopup: boolean }>(({ theme, isFromPreviewPopup }) => ({
  [`&.${styleClasses.toggleBox}`]: {
    borderRadius: "20px",
    position: "relative",
    background: theme.palette.neutral.brightGray,
    boxShadow: "1px 1px 2px rgb(165, 165, 165) inset",
    width: "inherit",
    display: "flex",
    "& .MuiButton-root": {
      borderRadius: "20px",
      minWidth: isFromPreviewPopup ? 47 : 50,
      height: isFromPreviewPopup ? 30 : 40,
      fontWeight: "bold",
      fontSize: isFromPreviewPopup ? "0.6rem" : "0.875rem",
      transition: "all 0.2s 0.1s ease",
      padding: 0,
    },
    "& .MuiButton-root:hover": {
      opacity: 0.8,
    },
  },
}));

export const StyledToggleOnButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "toggleType",
})<{ toggleType: string }>(({ toggleType, theme }) => ({
  [`&.${styleClasses.toggleOn}`]: {
    color:
      toggleType === "on"
        ? theme.palette.neutral.brightGray
        : theme.palette.neutral.secondaryDark,
  },
}));

export const StyledToggleOffButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "toggleType",
})<{ toggleType: string }>(({ toggleType, theme }) => ({
  [`&.${styleClasses.toggleOff}`]: {
    color:
      toggleType === "off"
        ? theme.palette.neutral.brightGray
        : theme.palette.neutral.secondaryDark,
  },
}));

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "toggleType" && prop !== "isFromPreviewPopup",
})<{
  toggleType: string;
  isFromPreviewPopup: boolean;
}>(({ toggleType, theme, isFromPreviewPopup }) => {
  const containerWidth = isFromPreviewPopup ? 94 : 100;
  const boxWidth = isFromPreviewPopup ? 47 : 50;
  const translateX = toggleType === "on" ? 0 : containerWidth - boxWidth;

  return {
    [`&.${styleClasses.box}`]: {
      width: boxWidth,
      height: isFromPreviewPopup ? 30 : 42,
      borderRadius: 20,
      background: theme.palette.neutral.blue,
      position: "absolute",
      boxShadow: "1px 0 2px " + theme.palette.neutral.boxShadow,
      transition: "all 0.5s ease",
      transform: `translateX(${translateX}px)`,
    },
  };
});

export const StyledMainContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFromPreviewPopup",
})<{ isFromPreviewPopup: boolean }>(({ isFromPreviewPopup }) => ({
  [`&.${styleClasses.mainContainer}`]: {
    display: "flex",
    height: isFromPreviewPopup ? 30 : 40,
    width: isFromPreviewPopup ? 94 : 100,
  },
}));

interface OnOffToggleProps {
  toggleType: "on" | "off" | "show" | "hide";
  onToggleChange: (toggle: "on" | "off" | "show" | "hide") => void;
  isFromPreviewPopup?: boolean;
  labels?: {
    on: { id: string; defaultMessage: string };
    off: { id: string; defaultMessage: string };
  };
}

const OnOffToggle: React.FC<OnOffToggleProps> = ({
  toggleType,
  onToggleChange,
  isFromPreviewPopup = false,
  labels,
}) => {
  const intl = useIntl();

  // Determine if we're using "hide/show" mode or "on/off" mode
  const isHideShowMode = toggleType === "hide" || toggleType === "show";
  // Left button is selected when toggleType is "on" or "hide"
  const isOnState = toggleType === "on" || toggleType === "hide";
  // Values to return when each button is clicked
  const onValue = isHideShowMode ? "hide" : "on";
  const offValue = isHideShowMode ? "show" : "off";

  return (
    <StyledMainContainer
      className={styleClasses.mainContainer}
      isFromPreviewPopup={isFromPreviewPopup}
    >
      <StyledToggleBox
        className={styleClasses.toggleBox}
        isFromPreviewPopup={isFromPreviewPopup}
      >
        <StyledBox
          toggleType={isOnState ? "on" : "off"}
          className={styleClasses.box}
          isFromPreviewPopup={isFromPreviewPopup}
        />
        <StyledToggleOnButton
          className={styleClasses.toggleOn}
          toggleType={isOnState ? "on" : "off"}
          disableRipple
          variant="text"
          onClick={() =>
            onToggleChange(onValue as "on" | "off" | "show" | "hide")
          }
        >
          {intl.formatMessage(
            labels?.on || {
              id: "productAlertsScheduleModal.toggleOn",
              defaultMessage: "on",
            },
          )}
        </StyledToggleOnButton>
        <StyledToggleOffButton
          className={styleClasses.toggleOff}
          toggleType={isOnState ? "on" : "off"}
          disableRipple
          variant="text"
          onClick={() =>
            onToggleChange(offValue as "on" | "off" | "show" | "hide")
          }
        >
          {intl.formatMessage(
            labels?.off || {
              id: "productAlertsScheduleModal.toggleOff",
              defaultMessage: "off",
            },
          )}
        </StyledToggleOffButton>
      </StyledToggleBox>
    </StyledMainContainer>
  );
};

export default OnOffToggle;
